/**
 * `Umpires` — the owning manager's own umpires, with where each report and marking stands.
 *
 * Authority: `um-app/06-report-studio.md` §4.6 (the `Also Waiting` queue — *"the owner's
 * remaining reports, one row per umpire, not per competition"*), `05-reporting-marking.md`
 * §B3b, and `um-app/00-overview.md` §10, which gives the bottom bar its `Umpires` tab.
 *
 * **What this list is, and what it is not.** Spec 04 §2.1 asks the `Umpires` tab to land on a
 * picker over the whole panel of ~30 umpires, as the way into each umpire's evidence timeline.
 * That timeline is not built, and a picker into a screen that does not exist would be a row of
 * dead links. What is built is the surface this slice is for: **the umpires this manager owns**
 * — the ones whose report and marks are hers to write, mark and push (§A5). When the timeline
 * lands it joins these rows rather than replacing them; an owned umpire is reached the same way
 * either day.
 *
 * One row per umpire for the whole tournament (ruling R1′): no `MEN` / `WOM` tag, no second row
 * for an umpire who worked both competitions, and no per-competition anything.
 *
 * Phone-first: the rows are `.umrow` cards with a full-width tap target each, not a table.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { REPORT_STAGE_LABELS, REPORT_STAGE_TOOLTIPS } from "@hockeytd/shared";

import { fetchMyUmpires, fetchUmConsolidatedEvidence } from "../../api/client.js";
import type { UmConsolidatedEvidenceBody, UmUmpireRow } from "../../api/types.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { useTournament } from "../../app/session.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import { exportWorkbook, type WorkbookExporter } from "../../features/export/render.js";
import {
  consolidatedEvidenceRows,
  consolidatedScopeLine,
  consolidatedSummaryRows,
  consolidatedSummaryScopeLine,
} from "../../features/export/rows.js";
import {
  buildConsolidatedEvidenceWorkbook,
  exportFilename,
  exportNotice,
} from "../../features/export/workbook.js";

/** The marking half of a row, in words rather than a bar that would imply a quality score. */
export function markingSummary(row: UmUmpireRow): string {
  if (row.markedCriteria === 0) return "Not Marked";
  if (!row.marksComplete) {
    return `${row.markedCriteria} Of ${row.criteriaCount} Categories`;
  }
  return row.outcome === null
    ? `All ${row.criteriaCount} Categories`
    : `All ${row.criteriaCount} Categories · Outcome ${row.outcome}`;
}

/**
 * The evidence half of a row — §4.6's `18 Notes`, with the readiness ring's figure beside it.
 *
 * `Not Enough Evidence` is one of §4.6's four statuses, and it is said in words here rather
 * than drawn as a ring: at row scale a donut would be decoration, and the figure it carries
 * (`readiness`) is the same figure the studio's ring draws from the same field.
 */
export function evidenceSummary(row: UmUmpireRow): string {
  if (row.releasedNoteCount === 0) return "Not Enough Evidence";
  const notes = row.releasedNoteCount === 1 ? "1 Note" : `${row.releasedNoteCount} Notes`;
  return `${notes} · ${row.readiness.covered}/${row.readiness.total} Criteria Covered`;
}

function UmpireRow({ umpire }: { umpire: UmUmpireRow }): React.JSX.Element {
  return (
    <li className="umrow" data-testid={`umpire-${umpire.tmsPersonId}`}>
      <div className="umrow__who">
        <span className="umrow__nm">{umpire.mediaName}</span>
        <span className="umrow__sub">
          {umpire.country}
          {umpire.cap === null ? "" : ` · ${umpire.cap} ${umpire.capUnit ?? "matches"}`} · Whole
          Tournament
        </span>
      </div>

      <div className="umrow__state">
        <span
          className="statuspill statuspill--warn"
          title={REPORT_STAGE_TOOLTIPS[umpire.stage]}
          data-testid={`stage-${umpire.tmsPersonId}`}
        >
          <span className="sdot" />
          {REPORT_STAGE_LABELS[umpire.stage]}
        </span>
        <span className="umrow__marks" data-testid={`marks-${umpire.tmsPersonId}`}>
          {markingSummary(umpire)}
        </span>
        {/* §4.6's evidence count, and spec 04 §2.1's readiness indicator on the picker row.
            Released evidence only — a manager's own locked notes are hers and are shown on
            her evidence pane, never as a figure on a list. */}
        <span className="umrow__marks" data-testid={`evidence-${umpire.tmsPersonId}`}>
          {evidenceSummary(umpire)}
        </span>
      </div>

      <div className="umrow__go">
        <Link className="gbtn" to={`${UM_ROOT_PATH}/umpires/${umpire.tmsPersonId}/marking`}>
          Marking
        </Link>
        <Link
          className="gbtn gbtn--solid"
          to={`${UM_ROOT_PATH}/umpires/${umpire.tmsPersonId}/report`}
        >
          Report
        </Link>
      </div>
    </li>
  );
}

/**
 * The two seams this page holds — the consolidated-evidence read and the workbook writer.
 *
 * Both are injectable for the same reason `AppointmentsPanel`'s exporter is: a page test
 * asserts the FILENAME and the SPEC the page assembled, and the builder tests assert what
 * that spec becomes. Nothing is proven twice, and the spreadsheet writer is never loaded in
 * a page test — which is also the point of the seam, since it is only fetched on a press.
 */
export interface UmUmpiresPageProps {
  exporter?: WorkbookExporter;
  fetchConsolidated?: () => Promise<UmConsolidatedEvidenceBody>;
}

export function UmUmpiresPage({
  exporter = exportWorkbook,
  fetchConsolidated = fetchUmConsolidatedEvidence,
}: UmUmpiresPageProps = {}): React.JSX.Element {
  const mine = useAsync(() => fetchMyUmpires(), []);
  const tournament = useTournament();
  const [exporting, setExporting] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Spec 11 §5 — the whole evidence pool, as the workbook the reports get written from.
   *
   * The pool is fetched ON THE PRESS rather than held by the page: it carries the reader's
   * own unreleased notes and every released note about every umpire she owns, and a list
   * screen has no use for any of it until somebody asks for the file.
   *
   * An empty tournament still produces a workbook — banner, and one honest row saying so.
   * A button that silently did nothing would leave a manager unable to tell a failed
   * download from a fortnight in which nothing was recorded.
   */
  async function runExport(): Promise<void> {
    setExporting(true);
    setFailure(null);
    setNote(null);
    try {
      const body = await fetchConsolidated();
      // One instant for the sheet and the filename, on the tournament's own clock.
      const at = new Date();
      const timezone = tournament?.timezone ?? null;
      const rows = consolidatedEvidenceRows(body, timezone);
      const workbook = buildConsolidatedEvidenceWorkbook({
        tournament: tournament?.shortDisplayName ?? null,
        timezone,
        at,
        scope: consolidatedScopeLine(body.umpires.length, rows.length),
        summaryScope: consolidatedSummaryScopeLine(body.umpires.length),
        rows,
        summary: consolidatedSummaryRows(body),
      });
      const filename = exportFilename(workbook.base, at, timezone);
      await exporter(workbook, filename);
      setNote(exportNotice(filename));
    } catch {
      setFailure("The workbook could not be written. Nothing has been downloaded.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <header className="umsect">
        <h2>Your Umpires</h2>
        <span className="umsect__kicker">Final Marking · Whole Tournament</span>
        <button
          type="button"
          className="gbtn"
          data-testid="export-consolidated-evidence"
          onClick={() => void runExport()}
          aria-busy={exporting}
          disabled={exporting || mine.status !== "ready"}
          title={
            "Download every note behind these reports as a spreadsheet — released notes " +
            "from the whole panel, plus your own Drafts and Locked notes. The file is yours " +
            "alone."
          }
        >
          {exporting ? "Preparing…" : "Download All Feedback"}
        </button>
        <HowToUse id="um-umpires" title="How to use Your Umpires">
          <li>
            <HowToLink to="my-umpires">This list</HowToLink> is the umpires whose marks and
            end-of-tournament report are yours to write.
          </li>
          <li>
            Each row shows how far that report has got and how much released evidence stands
            behind it.
          </li>
          <li>
            <b>Marking</b> on a row opens that umpire&rsquo;s marks. <b>Report</b> opens their
            report.
          </li>
          <li>
            If a name is missing, the Technical Delegate has not assigned that umpire to you
            yet.
          </li>
          <li>
            <b>Download All Feedback</b> gives you every note behind these reports in one
            spreadsheet — the panel&rsquo;s released notes and your own Drafts and Locked
            notes, which nobody else has seen. That file is yours alone.
          </li>
        </HowToUse>
      </header>

      {note === null ? null : (
        <p className="umfoot" data-testid="export-note">
          {note}
        </p>
      )}
      {failure === null ? null : (
        <p className="umfoot" role="alert" data-testid="export-failure">
          {failure}
        </p>
      )}

      {mine.status === "loading" ? <Skeleton rows={4} /> : null}
      {mine.status === "error" ? <FailBanner error={mine.error} /> : null}

      {mine.status === "ready" ? (
        mine.data.umpires.length === 0 ? (
          <section className="card">
            <p className="card__body">
              No umpires have been assigned to you for this tournament yet. Ownership is
              assigned per umpire by the Technical Delegate; until it is, there is nothing here
              to write.
            </p>
          </section>
        ) : (
          <>
            <ul className="umlist" data-howto="my-umpires">
              {mine.data.umpires.map((umpire) => (
                <UmpireRow key={umpire.tmsPersonId} umpire={umpire} />
              ))}
            </ul>
            <p className="umfoot">
              {mine.data.umpires.length} umpires are yours to report on and mark. One report and
              one set of marks each, for the whole tournament — the marks are agreed with the
              Technical Delegate and pushed by you.
            </p>
          </>
        )
      ) : null}
    </>
  );
}
